import { inject, Injector, INJECTOR } from '@angular/core';
import { createInjectionToken, createNoopInjectionToken } from 'ngxtension/create-injection-token';

export type AuiLocalizationFn<T extends object | never | null = never> = (args?: T) => string;

export type AuiTranslations = {
    clearValueTooltip: AuiLocalizationFn;
    datepickerPlaceholder: AuiLocalizationFn;
    inputPlaceholder: AuiLocalizationFn;
    filterPlaceholder: AuiLocalizationFn;
    selectPlaceholder: AuiLocalizationFn;
    multiselectPlaceholder: AuiLocalizationFn;
    hours: AuiLocalizationFn;
    minutes: AuiLocalizationFn;
    seconds: AuiLocalizationFn;
    period: AuiLocalizationFn;
    noItems: AuiLocalizationFn;
    time: AuiLocalizationFn;
    day: AuiLocalizationFn;
    month: AuiLocalizationFn;
    year: AuiLocalizationFn;
    allTime: AuiLocalizationFn;
    error: AuiLocalizationFn;
    warning: AuiLocalizationFn;
    success: AuiLocalizationFn;
    info: AuiLocalizationFn;
    accept: AuiLocalizationFn;
    submit: AuiLocalizationFn;
    reject: AuiLocalizationFn;
    confirmationHeader: AuiLocalizationFn;
    confirmationMessage: AuiLocalizationFn;
    add: AuiLocalizationFn;
    sorting: AuiLocalizationFn;
    download: AuiLocalizationFn;
    preview: AuiLocalizationFn;
    fileDragAndDrop: AuiLocalizationFn;
    or: AuiLocalizationFn;
    fileBrowse: AuiLocalizationFn;
    fileTypeNotAllowed: AuiLocalizationFn;
    fileTooBig: AuiLocalizationFn;
    filesTooBig: AuiLocalizationFn;
    selectSorting: AuiLocalizationFn;
    searchKeyword: AuiLocalizationFn;
    loading: AuiLocalizationFn;
    spotlightFilterPlaceholder: AuiLocalizationFn;
    yes: AuiLocalizationFn;
    no: AuiLocalizationFn;
    checkAll: AuiLocalizationFn;
    uncheckAll: AuiLocalizationFn;
    apply: AuiLocalizationFn;
    more: AuiLocalizationFn;
    selectTime: AuiLocalizationFn;
    noDataAvailable: AuiLocalizationFn;
    notifications: AuiLocalizationFn;
    writeComment: AuiLocalizationFn;
    editComment: AuiLocalizationFn;
    listBoxCheckAll: AuiLocalizationFn;
    displayOptions: AuiLocalizationFn;
    language: AuiLocalizationFn;
    closeFullView: AuiLocalizationFn;
    logout: AuiLocalizationFn;
    dark: AuiLocalizationFn;
    light: AuiLocalizationFn;
    pageNotFoundTitle: AuiLocalizationFn;
    pageNotFoundText: AuiLocalizationFn;
    navigateBack: AuiLocalizationFn;
    error404Title: AuiLocalizationFn;
    error404Message: AuiLocalizationFn;
    error403Title: AuiLocalizationFn;
    error403Message: AuiLocalizationFn;
    error500Title: AuiLocalizationFn;
    error500Message: AuiLocalizationFn;
    entityValidation: AuiLocalizationFn;
    errorGenericTitle: AuiLocalizationFn;
    errorGenericMessage: AuiLocalizationFn;
    phoneInputPlaceholder: AuiLocalizationFn;
    phoneInputCodePlaceholder: AuiLocalizationFn;
    phoneInputFilterCodePlaceholder: AuiLocalizationFn;
    editorText: AuiLocalizationFn;
    editorHead1: AuiLocalizationFn;
    editorHead2: AuiLocalizationFn;
    editorHead3: AuiLocalizationFn;
    editorBulletedList: AuiLocalizationFn;
    editorNumberedList: AuiLocalizationFn;
    editorTable: AuiLocalizationFn;
    editorQuote: AuiLocalizationFn;
    editorTodoList: AuiLocalizationFn;
    editorToggleParagraph: AuiLocalizationFn;
    editorToggleHead1: AuiLocalizationFn;
    editorToggleHead2: AuiLocalizationFn;
    editorToggleHead3: AuiLocalizationFn;
    editorDivider: AuiLocalizationFn;
    editorYoutube: AuiLocalizationFn;
    editorUpload: AuiLocalizationFn;
    editorCodeBlock: AuiLocalizationFn;
    editorToggleCode: AuiLocalizationFn;
    editorSlashCommandType: AuiLocalizationFn;
    editorSlashCommandOnPage: AuiLocalizationFn;
    editorSlashCommandBasicBlocks: AuiLocalizationFn;
    editorSlashCommandMedia: AuiLocalizationFn;
    editorTableMenuRow: AuiLocalizationFn;
    editorTableMenuColummn: AuiLocalizationFn;
    editorTableMenuTable: AuiLocalizationFn;
    [key: string]: AuiLocalizationFn<never>;
};

export const [injectAuiTranslations, provideAuiTranslations] = createNoopInjectionToken<Partial<AuiTranslations>, true>(
    'Aui Translation',
    { multi: true }
);

export const [injectAuiTranslationDictionary, provideAuiTranslationDictionary] = createInjectionToken<
    () => AuiTranslations
>(() => {
    const injectRecursively = (
        injector: Injector | undefined | null,
        _prev: Partial<AuiTranslations>
    ): Partial<AuiTranslations> => {
        if (injector === undefined || injector === null) return _prev;
        const current = injectAuiTranslations({ optional: true, injector });
        if (current === null || current === undefined) return _prev;
        const flat = current.flat().reduce((a, b) => ({ ...a, ...b }), {});
        return injectRecursively(injector.get(INJECTOR, undefined, { skipSelf: true, optional: true }), {
            ...flat,
            ..._prev,
        });
    };

    return {
        clearValueTooltip: () => 'Clear control',
        datepickerPlaceholder: () => 'Select date',
        inputPlaceholder: () => 'Enter value',
        filterPlaceholder: () => 'Filter items',
        selectPlaceholder: () => 'Select item',
        multiselectPlaceholder: () => 'Select items',
        noItems: () => 'No items',
        period: () => 'Period',
        hours: () => 'Hours',
        minutes: () => 'Minutes',
        seconds: () => 'Seconds',
        time: () => 'Time',
        day: () => 'Day',
        month: () => 'Month',
        year: () => 'Year',
        allTime: () => 'All time',
        error: () => 'Error',
        warning: () => 'Warning',
        success: () => 'Success',
        info: () => 'Info',
        accept: () => 'Accept',
        reject: () => 'Reject',
        confirmationHeader: () => 'Are you sure?',
        confirmationMessage: () => 'Are you sure that you want to perform this action ?',
        add: () => 'Add',
        sorting: () => 'Sorting',
        download: () => 'Download',
        preview: () => 'Preview',
        fileDragAndDrop: () => 'Drag and drop file here',
        fileTypeNotAllowed: () => 'This file type is not allowed',
        filesTooBig: () => 'Total size exceeds maximum size limit',
        fileTooBig: () => 'File size exceeds maximum size limit',
        or: () => 'or',
        fileBrowse: () => 'Browse for file of maximum size',
        selectSorting: () => 'Select sorting',
        searchKeyword: () => 'Search keyword',
        loading: () => 'Loading',
        yes: () => 'Yes',
        no: () => 'No',
        checkAll: () => 'Check All',
        uncheckAll: () => 'Uncheck All',
        submit: () => 'Submit',
        spotlightFilterPlaceholder: () => 'Filter commands in spotlight',
        apply: () => 'Apply',
        more: () => 'More',
        selectTime: () => 'Select time',
        noDataAvailable: () => 'No data available',
        notifications: () => 'Notifications',
        aboutApplication: () => 'About application',
        writeComment: () => 'Write a comment',
        editComment: () => 'Edit comment',
        listBoxCheckAll: () => 'Select / Unselect all',
        displayOptions: () => 'Display',
        language: () => 'Language',
        closeFullView: () => 'Close full view',
        logout: () => 'Log out',
        dark: () => 'Dark',
        light: () => 'Light',
        navigateBack: () => 'Back',
        pageNotFoundTitle: () => 'Page does not exist',
        pageNotFoundText: () => 'Page was not found.',
        error404Title: () => 'Entity was not found',
        error404Message: () => 'Entity could have been moved or deleted.',
        error403Title: () => 'Unauthorized',
        error403Message: () => 'You are not authorized to view this page.',
        error500Title: () => 'Server error',
        error500Message: () => 'An error occurred on the server.',
        entityValidation: () => 'Invalid entity',
        errorGenericTitle: () => 'Error',
        errorGenericMessage: () => 'An unexpected error occurred',
        phoneInputPlaceholder: () => '+420 123 456 789',
        phoneInputCodePlaceholder: () => 'Code',
        phoneInputFilterCodePlaceholder: () => 'Filter code',
        editorText: () => 'Text',
        editorHead1: () => 'Head 1',
        editorHead2: () => 'Head 2',
        editorHead3: () => 'Head 3',
        editorBulletedList: () => 'Bulleted list',
        editorNumberedList: () => 'Numbered list',
        editorTable: () => 'Table',
        editorQuote: () => 'Quote',
        editorTodoList: () => 'Todo list',
        editorToggleParagraph: () => 'Toggle paragraph',
        editorToggleHead1: () => 'Toggle Head 1',
        editorToggleHead2: () => 'Toggle Head 2',
        editorToggleHead3: () => 'Toggle Head 3',
        editorDivider: () => 'Divider',
        editorYoutube: () => 'Youtube',
        editorUpload: () => 'Upload',
        editorCodeBlock: () => 'Code Block',
        editorToggleCode: () => 'Toggle code',
        editorSlashCommandType: () => 'Type:',
        editorSlashCommandOnPage: () => 'on Page',
        editorSlashCommandBasicBlocks: () => 'Basic blocks',
        editorSlashCommandMedia: () => 'Media',
        editorTableMenuRow: () => 'Row',
        editorTableMenuColummn: () => 'Column',
        editorTableMenuTable: () => 'Table',
        ...injectRecursively(inject(INJECTOR), {} as AuiTranslations),
    };
});
